import { type ReactNode, useCallback, useRef, useState } from "react";
import type { GrantEntry } from "../../shared/rbac/roles";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { ModalContent, ModalFooter } from "./ModalCard";
import type { ScopeAccessPendingHandle } from "./ScopeAccessTable";
import { Text } from "./Text";

type CloseIntent = "close" | "continue";

type PendingWarning = {
  proceed: (grants?: GrantEntry[]) => void;
  intent: CloseIntent;
  count: number;
  canCommit: boolean;
  blockedReason?: string;
};

export function useScopeAccessCloseWarning({
  singular,
  plural,
}: {
  singular: string;
  plural: string;
}): {
  stagedGrantsRef: { current: ScopeAccessPendingHandle | null };
  requestClose: (
    proceed: (grants?: GrantEntry[]) => void,
    intent?: CloseIntent
  ) => void;
  closeWarningModal: ReactNode;
} {
  const stagedGrantsRef = useRef<ScopeAccessPendingHandle | null>(null);
  const [showWarningModal, setShowWarningModal] =
    useState<PendingWarning | null>(null);

  const requestClose = useCallback(
    (
      proceed: (grants?: GrantEntry[]) => void,
      intent: CloseIntent = "close"
    ) => {
      const pending = stagedGrantsRef.current;
      if (pending && pending.pendingCount > 0) {
        setShowWarningModal({
          proceed,
          intent,
          count: pending.pendingCount,
          canCommit: pending.canCommit,
          blockedReason: pending.blockedReason,
        });
        return;
      }
      proceed();
    },
    []
  );

  const proceedAndDismiss = (warning: PendingWarning) => {
    setShowWarningModal(null);
    warning.proceed();
  };

  const closeWarningModal = showWarningModal ? (
    <Modal open onClose={() => setShowWarningModal(null)} alert>
      <ModalContent style={{ width: 360 }}>
        <Text variant="text-regular-md" color="var(--text-primary)">
          {showWarningModal.intent === "continue"
            ? "Add before continuing?"
            : "Add before closing?"}
        </Text>
        <Text variant="text-regular" color="var(--text-secondary)">
          You have {showWarningModal.count}{" "}
          {showWarningModal.count === 1 ? singular : plural}{" "}
          {showWarningModal.blockedReason
            ? `selected. ${showWarningModal.blockedReason}`
            : "selected but not yet added."}
        </Text>
        <ModalFooter>
          <Button
            size="l"
            variant="secondary"
            onClick={() => proceedAndDismiss(showWarningModal)}
          >
            Discard
          </Button>
          {showWarningModal.canCommit ? (
            <Button
              size="l"
              onClick={() => {
                const pending = stagedGrantsRef.current;
                if (!pending || pending.pendingCount === 0) {
                  proceedAndDismiss(showWarningModal);
                  return;
                }
                if (!pending.canCommit) {
                  setShowWarningModal({
                    ...showWarningModal,
                    count: pending.pendingCount,
                    canCommit: false,
                    blockedReason: pending.blockedReason,
                  });
                  return;
                }
                const { proceed } = showWarningModal;
                setShowWarningModal(null);
                // Proceed once the commit actually lands: immediately for a
                // plain add, or after a deferred grant modal is confirmed.
                pending.commitPending(proceed);
              }}
            >
              {showWarningModal.intent === "continue"
                ? "Add and continue"
                : "Add and close"}
            </Button>
          ) : (
            <Button size="l" onClick={() => setShowWarningModal(null)}>
              Keep editing
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  ) : null;

  return {
    stagedGrantsRef,
    requestClose,
    closeWarningModal,
  };
}
